import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { User, UserRole, UserStatus } from '@core/generated/gql-types';
import { useTenantStore } from '@core/tenant/useTenantStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useUserStore } from '@core/user/useUserStore';
import { formatUserDisplayName } from '@/utils/utils';
import AssignAdminConfirmModal from './AssignAdminConfirmModal';

interface AssignedAdminDropdownProps {
  worker: User;
  loading: boolean;
}

const AssignedAdminDropdown: React.FC<AssignedAdminDropdownProps> = ({
  worker,
  loading,
}) => {
  const [isAssigningAdmin, setIsAssigningAdmin] = useState(false);
  const [assignConfirmOpen, setAssignConfirmOpen] = useState(false);
  const [assignSelectValue, setAssignSelectValue] = useState(
    () => worker.assignedAdminId ?? '',
  );

  const { currentUser } = useUserStore();
  const { tenantUsers, getTenantUserById } = useTenantStore();
  const { fetchDetailedWorkerById, updateWorkerAssignedAdmin } =
    useWorkerStore();

  useEffect(() => {
    setAssignSelectValue(worker.assignedAdminId ?? '');
  }, [worker.assignedAdminId, worker.id]);

  const handleConfirmAssignAdmin = async (changeReason: string) => {
    setIsAssigningAdmin(true);
    try {
      await updateWorkerAssignedAdmin(
        worker.id,
        assignSelectValue,
        changeReason.trim() || undefined,
      );

      await fetchDetailedWorkerById(worker.id);

      toast.success(
        !assignSelectValue
          ? 'Assigned admin removed'
          : 'Assigned admin updated',
        { position: 'bottom-left', autoClose: 4000 },
      );

      setAssignConfirmOpen(false);

      setAssignSelectValue('');
    } catch (error) {
      console.error('Error assigning admin:', error);
      toast.error(
        `Failed to update assigned admin: ${error instanceof Error ? error.message : 'Error'}`,
        { position: 'bottom-left', autoClose: 5000 },
      );
      setAssignSelectValue(worker.assignedAdminId ?? '');
    } finally {
      setIsAssigningAdmin(false);
    }
  };

  const handleChangeAssignedAdmin = (next: string) => {
    const current = worker.assignedAdminId ?? '';
    if (next === current) return;
    setAssignSelectValue(next);
    setAssignConfirmOpen(true);
  };

  const handleCancelAssignAdmin = () => {
    setAssignConfirmOpen(false);
    setAssignSelectValue(worker.assignedAdminId ?? '');
  };

  const pendingAdminDisplayName = useMemo(() => {
    if (!assignSelectValue) {
      return 'no admin';
    }
    const user = getTenantUserById(assignSelectValue);
    return user ? formatUserDisplayName(user) : assignSelectValue;
  }, [assignSelectValue, getTenantUserById]);

  const assignableTenantUsers: User[] = useMemo(() => {
    // Only show active tenant users as available options.
    const list =
      tenantUsers?.filter((user) => user.status === UserStatus.Active) ?? [];
    const sorted: User[] = [...list].sort((a, b) =>
      formatUserDisplayName(a).localeCompare(formatUserDisplayName(b)),
    );
    if (
      currentUser?.role === UserRole.TenantAdmin ||
      currentUser?.role === UserRole.TenantUser
    ) {
      // If the current user is a tenant admin or tenant user, show the current user as the first option.
      return [
        currentUser,
        ...sorted.filter((user) => user.id !== currentUser?.id),
      ];
    }
    // If the current user is not a tenant admin or tenant user, show all tenant users as available options.
    return sorted;
  }, [tenantUsers, currentUser]);

  const selected = assignSelectValue
    ? assignableTenantUsers.find((user) => user.id === assignSelectValue)
    : null;

  const buttonLabel = () => {
    if (loading) return 'Loading...';
    if (selected) return formatUserDisplayName(selected);
    if (assignSelectValue) return assignSelectValue;
    if (assignableTenantUsers.length === 0 && !loading)
      return 'No admins available';
    return 'Assign consultant';
  };

  const listDisabled = isAssigningAdmin || loading;

  // Only show the dropdown if the current user is a tenant admin or hq admin.
  // Tenant users cannot assign admins at this time.
  if (
    currentUser?.role !== UserRole.TenantAdmin &&
    currentUser?.role !== UserRole.HqAdmin
  ) {
    return null;
  }

  return (
    <>
      <Listbox
        value={assignSelectValue}
        onChange={handleChangeAssignedAdmin}
        disabled={listDisabled}
      >
        <div className="relative w-full min-w-0 text-left">
          <Listbox.Button
            className={`relative w-full max-w-70 min-w-0 cursor-default overflow-hidden rounded-lg border border-gray-300 bg-white py-2 pr-10 pl-3 text-left focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm`}
            disabled={listDisabled}
          >
            <span className="block min-w-0 truncate">{buttonLabel()}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="ring-opacity-5 absolute z-10 mt-1 max-h-80 w-full min-w-0 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black focus:outline-hidden sm:text-sm">
              <Listbox.Option
                key="none"
                className={({ active }) =>
                  `relative cursor-default py-2 pr-4 pl-10 select-none ${
                    active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                  }`
                }
                value={''}
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                    >
                      Unassign
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="h-5 w-5"
                          aria-hidden
                        />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
              {assignableTenantUsers.map((user) => (
                <Listbox.Option
                  key={user.id}
                  className={({ active }) =>
                    `relative cursor-default py-2 pr-4 pl-10 select-none ${
                      active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                    }`
                  }
                  value={user.id}
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                      >
                        {formatUserDisplayName(user) +
                          (currentUser?.id === user.id
                            ? ' (Assign to me)'
                            : '')}
                      </span>
                      {selected ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="h-5 w-5"
                            aria-hidden
                          />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>

      <AssignAdminConfirmModal
        isOpen={assignConfirmOpen}
        onClose={handleCancelAssignAdmin}
        onConfirm={handleConfirmAssignAdmin}
        adminDisplayName={pendingAdminDisplayName}
        worker={worker}
        isClearing={assignSelectValue === '' && !!worker.assignedAdminId}
        isSubmitting={isAssigningAdmin}
      />
    </>
  );
};

export default AssignedAdminDropdown;
